import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useDjRestAuthLoginCreate } from "@/api/lctApIComponents";
import { useRouter, Link } from "@tanstack/react-router";
import { useToast } from "@/hooks/use-toast";
import { useAppDispatch } from "@/hooks/reduxHooks";
import { login } from "@/auth/state/userSlice";
import { useState } from "react";

interface LoginFormProps {
  email?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ email }) => {
  const { toast } = useToast();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const formSchema = z.object({
    email: z.string().email(),
    password: z.string().min(3),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email ?? "",
      password: "",
    },
  });

  const loginMutation = useDjRestAuthLoginCreate({
    onSuccess: (data) => {
      dispatch(login({ email: data.user.email, key: data.key, isStaff: data.user.is_staff ?? false }));
      router.invalidate();
    },
    onError() {
      toast({
        variant: "destructive",
        title: "Error!",
        description: "Incorrect details",
      });
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    loginMutation.mutate({ body: { email: values.email, password: values.password } });
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-heading text-[32px] font-semibold leading-[48px] text-black">Log In</h1>
        <p className="font-body text-sm font-normal leading-6 text-gray-800">Enter your account details to Log In</p>
      </div>

      <Form {...form}>
        <form id="login" onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="johnsmith@gmail.com"
                    {...field}
                    type="email"
                    autoComplete="username email"
                    className="h-12 w-full rounded-md border border-slate-400 bg-white px-3 py-3 font-body text-base font-normal leading-7 text-slate-900"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      {...field}
                      autoComplete="current-password"
                      spellCheck="false"
                      autoCapitalize="none"
                      className="h-12 w-full rounded-md border border-slate-400 bg-white px-3 py-3 pr-10 font-body text-base font-normal leading-7 text-slate-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              disabled={loginMutation.isPending}
              className="h-10 w-full rounded-md bg-[#2d6532] px-4 py-2 font-body text-sm font-semibold leading-6 text-white hover:bg-[#2d6532]/90"
            >
              {loginMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Log In
            </Button>
            <Link to="/forgot-password" className="text-center font-body text-sm font-semibold leading-6 text-gray-600 underline">
              Forgot your password?
            </Link>
          </div>
        </form>
      </Form>

      <div className="flex flex-wrap items-center justify-center gap-2 text-center font-body text-sm leading-6">
        <span className="font-normal text-gray-800">Don&apos;t have an account?</span>
        <Link to="/create-account" className="font-semibold text-[#2d6532] underline">
          Create an account
        </Link>
      </div>
    </div>
  );
};
